/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { parseImportCSV, ensureUniqueSku, generateUniqueSku, deleteStorageImages } from "../utils";

export function useItemHandlers({ user, myProfile, items, setItems, setTotalCount, refreshAllItemsStats, fields, showToast }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickForm, setQuickForm] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [recentlyUploaded, setRecentlyUploaded] = useState(new Set());
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [activityLog, setActivityLogLocal] = useState([]);

  const logActivity = async (action, item) => {
    const entry = { user_email: user.email, action, item_id: String(item?.id || ""), item_sku: item?.sku || "", details: { text: `${action} ${item?.sku || ""} ${item?.brand || ""}`.trim(), brand: item?.brand || "", sku: item?.sku || "" }, created_at: new Date().toISOString() };
    const { error } = await supabase.from("activity_log").insert(entry);
    if (!error) setActivityLogLocal(p => [entry, ...p.slice(0, 199)]);
  };

  // Comments loading
  useEffect(() => {
    if (!selected) { setComments([]); setCommentText(""); return; }
    setCommentsLoading(true);
    supabase.from("item_comments").select("*").eq("item_id", String(selected.id)).order("created_at", { ascending: true })
      .then(({ data }) => { setComments(data || []); setCommentsLoading(false); });
  }, [selected?.id]);

  const addComment = async () => {
    if (!commentText.trim() || !selected) return;
    const entry = { item_id: String(selected.id), user_email: user.email, text: commentText.trim(), created_at: new Date().toISOString() };
    const { data: inserted, error } = await supabase.from("item_comments").insert(entry).select().single();
    if (!error) { setComments(p => [...p, inserted || { ...entry, id: String(Date.now()) }]); setCommentText(""); }
  };

  const handleAdd = async () => {
    if (form.brand === "Other" && form.brandCustom) {
      setForm(p => ({ ...p, brand: p.brandCustom, brandCustom: undefined }));
    }
    const finalBrand = (form.brand === "Other" && form.brandCustom) ? form.brandCustom : form.brand;
    if (!finalBrand) { showToast("Brand is required", true); return; }
    setSaving(true);
    let finalSku = form.sku;
    if (finalSku) finalSku = await ensureUniqueSku(finalSku, finalBrand, null);
    else finalSku = await generateUniqueSku(finalBrand);
    const newItem = { id: String(Date.now()), ...form, sku: finalSku, brand: finalBrand, brandCustom: undefined, images: form.images || [], created_by: user.email, added_by_email: user.email, created_by_name: myProfile?.display_name || user.email?.split("@")[0] };
    const { error } = await supabase.from("items").insert({ id: newItem.id, data: newItem, created_at: new Date().toISOString() });
    setSaving(false);
    if (error) { showToast("Error saving item: " + error.message, true); return; }
    setItems(p => [newItem, ...p]);
    setTotalCount(c => c + 1);
    setRecentlyUploaded(prev => new Set(prev).add(newItem.id));
    setTimeout(() => setRecentlyUploaded(prev => { const n = new Set(prev); n.delete(newItem.id); return n; }), 15000);
    refreshAllItemsStats();
    setForm({}); setShowAdd(false); setShowQuickAdd(false); setQuickForm({});
    logActivity("Added", newItem);
    showToast(`"${newItem.brand} · ${newItem.sku}" added`);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    let finalEditing = { ...editing };
    if (finalEditing.sku) finalEditing.sku = await ensureUniqueSku(finalEditing.sku, finalEditing.brand, finalEditing.id);
    const { error } = await supabase.from("items").update({ data: finalEditing }).eq("id", String(finalEditing.id));
    setSaving(false);
    if (error) { showToast("Error updating item: " + error.message, true); return; }
    setItems(p => p.map(i => i.id === finalEditing.id ? finalEditing : i));
    refreshAllItemsStats();
    setRecentlyUploaded(prev => new Set(prev).add(finalEditing.id));
    setTimeout(() => setRecentlyUploaded(prev => { const n = new Set(prev); n.delete(finalEditing.id); return n; }), 15000);
    setSelected(finalEditing); setEditing(null);
    logActivity("Edited", finalEditing);
    showToast("Frame updated");
  };

  const handleDelete = async (id, name) => {
    const item = items.find(i => i.id === id);
    const { error } = await supabase.from("items").delete().eq("id", String(id));
    if (error) { showToast("Error deleting item: " + error.message, true); return; }
    if (item?.images?.length) await deleteStorageImages(item.images);
    logActivity("Deleted", item);
    setItems(p => p.filter(i => i.id !== id));
    setTotalCount(c => Math.max(0, c - 1));
    refreshAllItemsStats();
    setSelected(null);
    showToast(`"${name}" removed`);
  };

  const handleImportFile = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target.result;
      if (file.name.endsWith(".json")) {
        try {
          const parsed = JSON.parse(text);
          const imported = Array.isArray(parsed) ? parsed : (parsed.items || []);
          if (!imported.length) { showToast("No items found in JSON file", true); return; }
          setImportPreview(imported.map((item, i) => ({ ...item, id: item.id || Date.now() + i, images: Array.isArray(item.images) ? item.images : [] })));
        } catch { showToast("Invalid JSON file", true); }
        return;
      }
      const imported = parseImportCSV(text, fields);
      if (!imported.length) { showToast("No valid items found", true); return; }
      setImportPreview(imported);
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const handleImportConfirm = async (mode) => {
    if (!importPreview?.length) return;
    setSaving(true);
    if (mode === "replace") {
      await supabase.from("items").delete().neq("id", "0");
    }
    const rows = importPreview.map(item => ({ id: String(item.id), data: item, created_at: new Date().toISOString() }));
    for (let i = 0; i < rows.length; i += 50) await supabase.from("items").upsert(rows.slice(i, i + 50));
    if (mode === "replace") { setItems(importPreview); setTotalCount(importPreview.length); }
    else { setItems(p => [...importPreview, ...p]); setTotalCount(c => c + importPreview.length); }
    refreshAllItemsStats();
    setImportPreview(null); setShowImport(false); setSaving(false);
    showToast(`${importPreview.length} items ${mode === "replace" ? "imported (replaced)" : "added"}`);
  };

  return {
    saving, form, setForm, editing, setEditing,
    showAdd, setShowAdd, showQuickAdd, setShowQuickAdd, quickForm, setQuickForm,
    showImport, setShowImport, importPreview, setImportPreview,
    imageUploading, setImageUploading, selected, setSelected,
    recentlyUploaded, comments, commentText, setCommentText, commentsLoading,
    activityLog, setActivityLog: setActivityLogLocal,
    addComment, handleAdd, handleSaveEdit, handleDelete, handleImportFile, handleImportConfirm, logActivity,
  };
}
